import { Alert, Button, Collapse, Group, Stack, Title } from '@common/ui'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { com } from '@eidu/web'
import FieldsStack from '../../components/data/FieldsStack'
import { requireNotUndefinedOrNull } from '../../util/require'
import useEntityType from '../../io/useEntityType'
import { useSortedEntityTypes } from '../../io/useEntityTypes'
import AuditLogDrawer from '../audit/AuditLogDrawer'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import entityTypeIdFromString = com.eidu.entity.type.entityTypeIdFromString

type ViewEntityTypePageContentProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onEdit: () => void
}

const ViewEntityTypePageContent = ({ entityType, entityTypes, onEdit }: ViewEntityTypePageContentProps) => (
  <Stack gap="lg" p="lg">
    <Group justify="space-between">
      <Title order={2}>{entityType.name}</Title>
      <Group gap="xs">
        <AuditLogDrawer targetId={entityType.id.toString()} />
        <Button variant="subtle" onClick={onEdit}>
          Edit
        </Button>
      </Group>
    </Group>
    <FieldsStack entityType={entityType} entityTypes={entityTypes} />
  </Stack>
)

type ViewEntityTypePageProps = {
  entityTypeId: EntityTypeId
}

const ViewEntityTypePage = ({ entityTypeId }: ViewEntityTypePageProps) => {
  const navigate = useNavigate()

  const { entityType: maybeEntityType, error: typeError } = useEntityType(entityTypeId)
  const entityType = maybeEntityType ?? undefined
  const entityTypes = useSortedEntityTypes() ?? []
  const error = typeError

  return (
    <Stack gap={0}>
      <Collapse expanded={!!error}>
        <Alert
          variant="filled"
          severity="negative"
          m="md"
          action={
            <Button variant="white" size="xs" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <div>{String(error)}</div>
          <div>Please reload the page and try again.</div>
        </Alert>
      </Collapse>
      {entityType && (
        <ViewEntityTypePageContent
          entityType={entityType}
          entityTypes={entityTypes}
          onEdit={() => navigate('edit', { relative: 'path' })}
        />
      )}
    </Stack>
  )
}

const ViewEntityTypePageRoute = () => {
  const params = useParams()
  const entityTypeId = entityTypeIdFromString(
    requireNotUndefinedOrNull(params.entityTypeId, 'Entity type ID is required')
  )

  return <ViewEntityTypePage entityTypeId={entityTypeId} />
}

export default ViewEntityTypePageRoute
